import type { ReactElement } from 'react';
import React from 'react';
import clsx from 'clsx';
import ValueLabelBadge from 'feature-components/badges/value-label-badge/value-label-badge';
import type { BaseGender } from 'services/assist-api/entities/gender/genders';
import { useGenders } from 'services/assist-api/entities/gender/genders';
import './gender-badge.scss';

const genderToClassName = (gender: BaseGender): string =>
  gender.name.replace(' ', '-').toLowerCase();

/**
 * A Gender Badge component
 *
 * This is a more specific implementation of our generic Badge.
 * Intended use is displaying user genders e.g: male, female, another gender,
 * with some consistent styling for cross site recognition
 *
 * Also acts as a helper to go from `gender.id` -> `gender.name`
 */
export default function GenderBadge({
  id,
  hideLabel,
  isSmall
}: {
  id: BaseGender['id'];
  hideLabel?: boolean;
  isSmall?: boolean;
}): ReactElement | null {
  const genders = useGenders().data?.json;
  const gender = genders?.find((g) => g.id === id);

  if (!gender) return null;
  if (hideLabel) {
    return (
      <span
        className={clsx('badge gender-badge', genderToClassName(gender), {
          'p-1 fw-normal': isSmall
        })}
      >
        {gender.name}
      </span>
    );
  }
  return (
    <ValueLabelBadge
      label="Gender"
      value={gender.name}
      className={clsx('gender-badge', genderToClassName(gender), { 'p-1 fw-normal': isSmall })}
    />
  );
}
